import { Helmet } from "react-helmet-async";
import { Box } from "grommet";
import { useLocation, useParams } from "react-router-dom";

// Components
import CarbonGrid from "../../components/CarbonGrid/CarbonGrid";
import CarbonTabStrip from "../../components/CarbonTabs/CarbonTabStrip";
import CarbonGridToolbar, {
  GridToolbarActionLabels,
  GridToolbarItem
} from "../../components/CarbonGrid/Toolbar/CarbonGridToolbar";
import Authorizer from "../../components/Util/Authorizer";

// Types & Constants
import { GridType, GridColumns } from "../../types/grid";
import { gridColumns } from "./MediaMovieCodesGridColumns";
import getTabs from "./MediaLibraryTabs";

import {
  Placeholders,
  UserPermissions,
  GridIDs,
  EndpointResources,
  CarbonIcons
} from "../../constants";
import { useEffect, useState } from "react";

// Contexts
import { useGrid } from "../../contexts/grid/useGrid";
import { useStore } from "../../contexts/store";
import CarbonBreadcrumb from "../../components/Breadcrumb/CarbonBreadcrumb";

const MediaMovieCodes = (): JSX.Element => {
  const { pathname } = useLocation();
  const { mediaId } = useParams();
  const { grids } = useGrid();
  const { store } = useStore();

  const pagePermission = UserPermissions.MediaMovieCodesPerms;
  const tabs = getTabs(EndpointResources.Media, mediaId!);
  const gridName = GridIDs.MediaLibraryCodes;

  const [addBtn, setAddBtn] = useState<GridToolbarItem | undefined>();
  const [editModeBtn, setEditModeBtn] = useState<GridToolbarItem | undefined>();

  // Grid should only be editable if the parent media record is a movie poster and is either not shared or is shared and the user's owning company
  //  is Deel (CompanyID = 0)
  useEffect(() => {
    const row: any = grids.get(gridName)?.state.parentSelectedRowData;

    const isMoviePoster = row && row.IsMoviePoster === true;
    const isShared = row && row.IsShared === true;
    const isDeelUser = store.user?.companyID === 0;
    const allowInsertAndEdit =
      isMoviePoster && (!isShared || (isShared && isDeelUser));

    if (allowInsertAndEdit) {
      // without this, the buttons don't come back once they're gone unless you refresh
      setAddBtn(undefined);
      setEditModeBtn(undefined);
    } else {
      setAddBtn({
        show: false,
        enable: false,
        tooltip: ""
      });
      setEditModeBtn({
        show: false,
        enable: false,
        tooltip: ""
      });
    }
  }, [grids.get(gridName)?.state.parentSelectedRowData]);

  const actionLabels: GridToolbarActionLabels = {
    singular: "movie code",
    plural: "movie codes",
    titleSingular: "Movie Code",
    titlePlural: "Movie Codes"
  };

  const currentTab = {
    index: tabs.findIndex((tab) => tab.resourceEndpoint === pathname),
    tab: tabs.find((tab) => tab.resourceEndpoint === pathname)
  };

  const gridColumnsMovieCodes: GridColumns[] = [
    {
      field: "MediaID",
      title: "MediaID",
      width: "150px",
      defaultShow: false,
      filter: "numeric",
      editable: false,
      required: false,
      canBeSaved: true,
      defaultValue: parseInt(mediaId),
      systemHidden: true
    },
    ...gridColumns
  ];

  const gridSettings: GridType = {
    endpoints: {
      gridODataEndpoint: `/odata/${Placeholders.companyID}/${Placeholders.mediaTabMediaId}/gridmediamoviecodes?$count=true&`,
      gridApiEndpoint: `/api/${Placeholders.mediaTabMediaId}/mediamoviecodes`,
      parentGridApiEndpoint: `/api/${Placeholders.companyID}/${EndpointResources.Media}/${Placeholders.mediaTabMediaId}`
    },
    records: [],
    total: 0,
    data: [],
    lookups: {},
    dataState: {
      pageSize: 25,
      take: 50,
      skip: 0,
      sort: []
    },
    dataItemKey: "MediaMoviePosterID",
    singularEntityName: actionLabels.singular,
    pluralEntityName: actionLabels.plural,
    columns: gridColumnsMovieCodes,
    showCopyBtn: false,
    showDeleteBtn: true,
    parentGridId: GridIDs.MediaLibrary,
    state: {
      editMode: false,
      insertMode: false,
      selectedState: {},
      selectedRow: -1,
      showQuickFilter: false,
      lastSaveDate: null,
      showSavingIndicator: false,
      lockoutMode: false
    }
  };

  return (
    <>
      <Helmet>
        <title>Carbon | MediaLibrary</title>
      </Helmet>
      <Authorizer
        canView={pagePermission}
        auditLabel={`${actionLabels.titlePlural} screen`}
      >
        <Box align="start" fill>
          <CarbonBreadcrumb
            backLinkTitle="Media"
            backLinkUrl="/app/media"
            pageTitle={currentTab.tab?.tabTitle ?? ""}
          />
          <CarbonGridToolbar
            gridId={GridIDs.MediaLibraryCodes}
            screenIcon={CarbonIcons.MovieCodes}
            getScreenTitleFromParentRow={(rowData: { [key: string]: any }) => {
              return rowData.MediaName;
            }}
            getScreenSubtitleFromParentRow={(rowData: {
              [key: string]: any;
            }) => {
              return rowData.CompanyName;
            }}
            permissions={pagePermission}
            actionLabels={actionLabels}
            addBtn={addBtn}
            editModeBtn={editModeBtn}
          />
          <CarbonTabStrip carbonTabs={tabs} selectedTab={currentTab.index} />
          <Box fill className="carbon-grid-wrapper">
            <CarbonGrid gridId={gridName} gridSettings={gridSettings} />
          </Box>
        </Box>
      </Authorizer>
    </>
  );
};

export default MediaMovieCodes;
